import {
  claimPromotion,
  fetchPromotionDetail,
  fetchPromotions,
  setPromotionBirthday,
} from '~/api/promo'
import type { PromoActivityDto, PromoCategoryDto, PromoDisplayState } from '~/types/api/promo'
import type { PromoActivity, PromoCategoryItem } from '../types'

/** display_state → 卡片样式类（小写，与 scoped CSS 保持一致） */
const STATE_CLASS: Record<PromoDisplayState, string> = {
  UNSET: 'unset',
  INCOMPLETE: 'incomplete',
  WAITING: 'waiting',
  IN_PROGRESS: 'progress',
  CLAIMABLE: 'claimable',
  CLAIM_PROCESSING: 'progress',
  CLAIMED: 'claimed',
  EXPIRED: 'claimed',
}

/** resource_code → 站内页面（前端维护，后端不校验） */
const RESOURCE_ROUTES: Record<string, string> = {
  rebate: '/promo?tab=rebate',
  quest: '/promo?tab=daily',
  vip: '/promo?tab=vip',
  rewards_rebate: '/promo?tab=rebate',
  rewards_quest: '/promo?tab=daily',
  rewards_vip: '/promo?tab=vip',
}

export function usePromoActivities() {
  const { t } = useI18n()
  const { info, success, error } = useNotify()
  const localePath = useLocalePath()
  const route = useRoute()
  const { openWalletPushed } = useWalletNav()
  const { load: loadOverview } = useRewardOverview()

  const categories = ref<PromoCategoryItem[]>([])
  const category = ref('')
  const items = ref<PromoActivity[]>([])
  const loading = ref(false)
  /** 已按该分类拉取过，避免 load 内回填 category 触发重复请求 */
  let lastQuery: string | null = null

  /** 详情抽屉：打开时拉详情接口拿 detail 图 */
  const detailOpen = ref(false)
  const detailLoading = ref(false)
  const selected = ref<PromoActivity | null>(null)

  /** 生日设置（成功后不可改，提交前 UI 二次确认） */
  const birthdayOpen = ref(false)
  const birthdaySubmitting = ref(false)

  const statusLabel = (state: PromoDisplayState) => t(`promo.acts.status.${state}`)

  /** 后台是否配置了可执行的跳转：NONE / 未知 code / OPEN_RESOURCE 缺 resource_code 都算未配置 */
  const hasJump = (action: PromoActivityDto['action']): boolean => {
    switch (action.code) {
      case 'DEPOSIT':
      case 'WITHDRAW':
      case 'GAME':
        return true
      case 'OPEN_RESOURCE':
        return Boolean(action.target?.resource_code)
      default:
        return false
    }
  }

  /** 按钮文字：查 display_state（§2.2），唯一需要分叉的地方 */
  const actionLabel = (dto: PromoActivityDto): string => {
    const { state, action, claim_mode: claimMode, template_code: templateCode } = dto
    const details = state.details ?? {}
    switch (state.display_state) {
      case 'UNSET':
        return t('promo.acts.action.UNSET')
      case 'INCOMPLETE':
        return hasJump(action) ? t(`promo.acts.action.${action.code}`) : ''
      case 'WAITING':
        if (details.remaining_days == null) return statusLabel('WAITING')
        if (details.remaining_days === 0) return t('promo.acts.todayAvailable')
        return templateCode === 'birthday'
          ? t('promo.acts.waitingBirthday', { days: details.remaining_days })
          : t('promo.acts.waitingDays', { days: details.remaining_days })
      case 'IN_PROGRESS':
        if (action.code === 'DEPOSIT' || action.code === 'WITHDRAW' || action.code === 'GAME') {
          return t(`promo.acts.action.${action.code}`)
        }
        if (action.code === 'OPEN_RESOURCE' && hasJump(action)) {
          return t('promo.acts.action.OPEN_RESOURCE')
        }
        if (details.achieved_days != null) {
          return t('promo.acts.achievedDays', { days: details.achieved_days })
        }
        if (details.remaining_days != null) {
          return details.remaining_days === 0
            ? t('promo.acts.todayAvailable')
            : t('promo.acts.remainingDays', { days: details.remaining_days })
        }
        return statusLabel('IN_PROGRESS')
      case 'CLAIMABLE':
        return claimMode === 'AUTO' ? t('promo.acts.action.AUTO') : t('promo.acts.action.CLAIM')
      case 'CLAIM_PROCESSING':
      case 'CLAIMED':
      case 'EXPIRED':
        return statusLabel(state.display_state)
      default:
        return ''
    }
  }

  const mapCategory = (cat: PromoCategoryDto): PromoCategoryItem => ({
    code: cat.category_code || cat.code || '',
    label: cat.name || cat.label || '',
    iconUrl: cat.icon_url || '',
    iconKey: cat.icon_key || '',
    icon: cat.icon || '🎁',
  })

  const mapActivity = (dto: PromoActivityDto): PromoActivity => ({
    activityId: dto.activity_id,
    activityCode: dto.activity_code,
    templateCode: dto.template_code,
    claimMode: dto.claim_mode,
    displayState: dto.state.display_state,
    statusLabel: statusLabel(dto.state.display_state),
    actionLabel: actionLabel(dto),
    actionEnabled: dto.action.enabled,
    action: dto.action,
    state: dto.state,
    media: dto.media ?? {},
    categoryName: dto.category?.name || dto.category?.label || '',
    name: dto.name ?? '',
    description: dto.description ?? '',
    reward: dto.reward,
    rule: dto.rule,
    schedule: dto.schedule,
    claimPolicy: dto.claim_policy,
    scope: dto.scope,
    terms: dto.terms ?? [],
    coverUrl: dto.media?.cover_url ?? '',
    coverKey: dto.media?.cover_key ?? '',
    detailUrl: dto.media?.detail_url ?? '',
    detailKey: dto.media?.detail_key ?? '',
    stateClass: STATE_CLASS[dto.state.display_state] ?? 'incomplete',
  })

  /** 列表刷新后把详情抽屉里选中活动的状态与按钮同步（保留详情接口独有的图、细则） */
  const syncSelected = () => {
    const cur = selected.value
    if (!cur) return
    const fresh = items.value.find(a => a.activityCode === cur.activityCode)
    if (!fresh) return
    selected.value = {
      ...cur,
      displayState: fresh.displayState,
      statusLabel: fresh.statusLabel,
      actionLabel: fresh.actionLabel,
      actionEnabled: fresh.actionEnabled,
      action: fresh.action,
      state: fresh.state,
      stateClass: fresh.stateClass,
    }
  }

  /** force：操作后强制重拉当前分类（领取 / 设置生日），否则同分类会被 lastQuery 守卫直接跳过 */
  const load = async (force = false) => {
    if (loading.value) return
    const current = category.value
    if (!force && current && current === lastQuery) return
    loading.value = true
    try {
      const res = await fetchPromotions({
        ...(current ? { category: current } : {}),
        page: 1,
        page_size: 50,
      })
      if (res.categories?.length) {
        categories.value = res.categories.map(mapCategory)
      }
      if (!current) {
        const first = categories.value[0]?.code
        if (first) {
          lastQuery = first
          category.value = first
        }
      } else {
        lastQuery = current
      }
      items.value = (res.items ?? []).map(mapActivity)
      syncSelected()
    } catch (e) {
      // 拦截器已 toast，这里只保证状态复位
      console.error('[promo] fetch list failed:', e)
    } finally {
      loading.value = false
    }
  }

  watch(category, newVal => {
    if (newVal && newVal !== lastQuery) {
      void load()
    }
  })

  /** 领取：FAILED 只提示不改卡片状态；CLAIMED/PROCESSING 重取列表刷新 */
  const claim = async (activity: PromoActivity) => {
    const cycleNo = activity.action.target?.cycle_no ?? activity.state.cycle_no ?? ''
    try {
      const res = await claimPromotion(activity.activityCode, cycleNo)
      if (res.status === 'CLAIMED') {
        success(t('promo.acts.claimSuccess'))
        await Promise.all([load(true), loadOverview()]) // 卡片状态 + 页签角标一起刷
      } else if (res.status === 'PROCESSING') {
        info(t('promo.acts.claimProcessing'))
        await Promise.all([load(true), loadOverview()])
      } else {
        error(t('promo.acts.claimFailed'))
      }
    } catch (e) {
      // 403/409 等由拦截器 toast
      console.error('[promo] claim failed:', e)
    }
  }

  /** 打开站内资源：映射表前端维护，未知值兜底提示 */
  const openResource = (code?: string) => {
    // 钱包走推入态（带返回按钮），与 DEPOSIT 一致
    if (code === 'wallet') {
      void openWalletPushed(route.fullPath)
      return
    }
    const path = code ? RESOURCE_ROUTES[code] : undefined
    if (path) {
      navigateTo(localePath(path))
      return
    }
    console.warn('[promo] unknown resource_code:', code)
    info(t('promo.acts.unsupportedResource'))
  }

  /** 按 action.code 分发点击行为（§2.3） */
  const runAction = async (activity: PromoActivity) => {
    if (!activity.actionEnabled) return
    switch (activity.action.code) {
      case 'DEPOSIT':
        await openWalletPushed(route.fullPath)
        break
      case 'WITHDRAW':
        await navigateTo(localePath('/wallet/withdraw'))
        break
      case 'GAME':
        await navigateTo(localePath('/'))
        break
      case 'SET_BIRTHDAY':
        birthdayOpen.value = true
        break
      case 'CLAIM':
        await claim(activity)
        break
      case 'OPEN_RESOURCE':
        openResource(activity.action.target?.resource_code)
        break
      case 'NONE':
      default:
        break
    }
  }

  const openDetail = async (activity: PromoActivity) => {
    selected.value = activity
    detailOpen.value = true
    detailLoading.value = true
    try {
      const detail = await fetchPromotionDetail(activity.activityCode)
      selected.value = mapActivity(detail)
    } catch (e) {
      console.error('[promo] fetch detail failed:', e)
    } finally {
      detailLoading.value = false
    }
  }

  const submitBirthday = async (birthday: string) => {
    birthdaySubmitting.value = true
    try {
      await setPromotionBirthday(birthday)
      success(t('promo.acts.birthdaySet'))
      birthdayOpen.value = false
      await load(true)
    } catch (e) {
      console.error('[promo] set birthday failed:', e)
    } finally {
      birthdaySubmitting.value = false
    }
  }

  onMounted(() => {
    void load()
  })

  return {
    categories,
    category,
    items,
    loading,
    load,
    runAction,
    detailOpen,
    detailLoading,
    selected,
    openDetail,
    birthdayOpen,
    birthdaySubmitting,
    submitBirthday,
  }
}
